/**
 * Session Manager for Real-time Features
 * Handles live session state and real-time synchronization
 */

import {
  getSession,
  getLessonSlides,
  getSessionParticipants,
  updateSessionSlide,
  getSlideSubmissions
} from '../../utils/supabase/queries';
import type { Session, Slide} from '../../utils/supabase/client';
import {
  subscribeToSession,
  unsubscribeFromSession,
  broadcastSlideChange,
  broadcastSessionEnd,
  type SlideChangePayload,
  type SubmissionPayload,
  type ParticipantPayload
} from '../../utils/supabase/realtime';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface SessionState {
  session: Session & { lessons?: any };
  slides: Slide[];
  participants: any[];
  currentSlide: number;
  submissions: any[];
}

export class LiveSessionManager {
  private channel: RealtimeChannel | null = null;
  private sessionId: string;
  private callbacks: {
    onSlideChange?: (slideNumber: number) => void;
    onNewParticipant?: (participant: ParticipantPayload) => void;
    onNewSubmission?: (submission: SubmissionPayload) => void;
    onStateUpdate?: (state: Partial<SessionState>) => void;
  };

  constructor(sessionId: string, callbacks: LiveSessionManager['callbacks'] = {}) {
    this.sessionId = sessionId;
    this.callbacks = callbacks;
  }

  /**
   * Initialize session and subscribe to real-time updates
   */
  async initialize(): Promise<SessionState> {
    // Load initial session data
    const session = await getSession(this.sessionId);
    const slides = await getLessonSlides(session.lesson_id);
    const participants = await getSessionParticipants(this.sessionId);

    // Subscribe to real-time updates
    this.channel = subscribeToSession(this.sessionId, {
      onSlideChange: (payload) => {
        this.callbacks.onSlideChange?.(payload.current_slide);
        this.callbacks.onStateUpdate?.({ currentSlide: payload.current_slide });
      },
      onNewParticipant: (payload) => {
        this.callbacks.onNewParticipant?.(payload);
      },
      onNewSubmission: (payload) => {
        this.callbacks.onNewSubmission?.(payload);
      }
    });

    const initialState: SessionState = {
      session,
      slides,
      participants,
      currentSlide: session.current_slide || 0,
      submissions: []
    };

    return initialState;
  }

  /**
   * Navigate to a specific slide
   */
  async goToSlide(slideNumber: number): Promise<void> {
    if (!this.channel) {
      throw new Error('Session not initialized');
    }

    // Update database
    await updateSessionSlide(this.sessionId, slideNumber);

    // Broadcast to all participants
    await broadcastSlideChange(this.channel, slideNumber);
  }

  /**
   * Go to next slide
   */
  async nextSlide(currentSlide: number, totalSlides: number): Promise<void> {
    if (currentSlide < totalSlides - 1) {
      await this.goToSlide(currentSlide + 1);
    }
  }

  /**
   * Go to previous slide
   */
  async previousSlide(currentSlide: number): Promise<void> {
    if (currentSlide > 0) {
      await this.goToSlide(currentSlide - 1);
    }
  }

  /**
   * Load submissions for current slide
   */
  async loadSlideSubmissions(slideId: string): Promise<any[]> {
    return getSlideSubmissions(this.sessionId, slideId);
  }

  /**
   * End session and notify all students
   */
  async endSession(): Promise<void> {
    if (this.channel) {
      await broadcastSessionEnd(this.channel, this.sessionId);
      // Small delay to ensure broadcast is sent before cleanup
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  /**
   * Cleanup and unsubscribe
   */
  async cleanup(): Promise<void> {
    if (this.channel) {
      await unsubscribeFromSession(this.channel);
      this.channel = null;
    }
  }
}

/**
 * Helper to calculate session statistics
 */
export interface SessionStats {
  totalParticipants: number;
  totalSubmissions: number;
  averageProgress: number;
  completionRate: number;
}

export async function calculateSessionStats(sessionId: string): Promise<SessionStats> {
  const participants = await getSessionParticipants(sessionId);
  
  const totalParticipants = participants.length;
  const averageProgress = totalParticipants > 0
    ? participants.reduce((sum, p) => sum + p.progress_percentage, 0) / totalParticipants
    : 0;
  
  const completionRate = totalParticipants > 0
    ? participants.filter(p => p.progress_percentage === 100).length / totalParticipants * 100
    : 0;

  // Get total submissions
  const { count: totalSubmissions } = await supabase
    .from('submissions')
    .select('id', { count: 'exact', head: true })
    .eq('session_id', sessionId);

  return {
    totalParticipants,
    totalSubmissions: totalSubmissions || 0,
    averageProgress,
    completionRate
  };
}

// Re-export for internal use
import { supabase } from '../../utils/supabase/client';
